import { useState } from 'react'
import { MdClose, MdAdd, MdDelete } from 'react-icons/md'
import {
  DEFAULT_PALLET_TYPE,
  NetworkRateCardZone,
  PALLET_UNIT,
  PalletDimensions,
  QuantityBreak,
  SERVICE_LEVELS,
  SHIPPING_UNITS,
  ServiceIdentity,
  findService,
  getDefaultPalletCaps,
  getPalletTypeLabel,
  getServiceLabel,
  isServiceLevel,
} from './types'
import { PalletVisualizer } from './PalletVisualizer'

interface Props {
  zone: NetworkRateCardZone
  service: ServiceIdentity
  onBreaksChange: (zoneId: string, service: ServiceIdentity, breaks: QuantityBreak[]) => void
  onUnitValueChange: (zoneId: string, service: ServiceIdentity, unitValue: number | null) => void
  onPalletCapsChange: (service: ServiceIdentity, palletDimensions: PalletDimensions) => void
  onServiceChange: (oldService: ServiceIdentity, newService: ServiceIdentity) => void
  onRemoveService: (service: ServiceIdentity) => void
  onClose: () => void
}

const EMPTY_CAPS: PalletDimensions = { height: null, width: null, depth: null, weight: null }
const SINGLE_BREAK: QuantityBreak = { min: 1, max: null, price: 0 }

const UNIT_LABELS: Record<string, string> = {
  Kg: 'Weight (kg)',
  FTL: 'Full truck loads',
  Hour: 'Hours',
  Mile: 'Distance (miles)',
  Day: 'Days',
}

export function QuantityBreaksPanel({
  zone,
  service,
  onBreaksChange,
  onUnitValueChange,
  onPalletCapsChange,
  onServiceChange,
  onRemoveService,
  onClose,
}: Props) {
  const pricing = findService(zone, service)
  const breaks = pricing?.breaks?.length ? pricing.breaks : [SINGLE_BREAK]
  const [useTiers, setUseTiers] = useState(breaks.length > 1)
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)
  const isPalletService = service.unit === PALLET_UNIT
  const isLegacyLevel = !isServiceLevel(service.serviceName)

  const changeBreaks = (next: QuantityBreak[]) => onBreaksChange(zone.id, service, next)

  const updateBreak = (index: number, field: keyof QuantityBreak, value: number | null) =>
    changeBreaks(breaks.map((b, i) => (i === index ? { ...b, [field]: value } : b)))

  const addBreak = () => {
    const lastBreak = breaks[breaks.length - 1]
    const newMin = lastBreak ? (lastBreak.max ? lastBreak.max + 1 : lastBreak.min + 1) : 1
    changeBreaks([...breaks, { min: newMin, max: null, price: 0 }])
  }

  const removeBreak = (index: number) => {
    if (breaks.length <= 1) return
    changeBreaks(breaks.filter((_, i) => i !== index))
  }

  const handleToggleTiers = () => {
    if (useTiers) {
      changeBreaks([breaks[0] ?? SINGLE_BREAK])
      setUseTiers(false)
    } else {
      setUseTiers(true)
    }
  }

  const changeLevel = (serviceName: string) => onServiceChange(service, { ...service, serviceName })

  const changeUnit = (unit: string) => {
    if (unit === PALLET_UNIT) {
      onServiceChange(service, { serviceName: service.serviceName, unit, palletType: service.palletType ?? DEFAULT_PALLET_TYPE })
      return
    }
    onServiceChange(service, { serviceName: service.serviceName, unit })
  }

  const changePalletType = (palletType: string) => onServiceChange(service, { ...service, palletType })

  const inputClass =
    'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <>
      <div
        className='fixed inset-0 z-drawerBackdrop transition-opacity duration-300 lg:opacity-0 lg:pointer-events-none bg-black/20'
        onClick={onClose}
        role='presentation'
      />

      <div
        className='fixed z-drawer bg-white shadow-xl flex flex-col overflow-hidden transition-transform duration-300 ease-out right-0 top-0 h-full w-[22rem] border-l border-gray-200 translate-x-0'
      >
        <div className='flex items-start justify-between px-5 py-4 bg-white gap-2'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <select
                value={service.serviceName}
                onChange={(e) => changeLevel(e.target.value)}
                className='text-sm font-semibold text-gray-900 w-full px-2 py-1.5 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white hover:border-gray-300 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-colors'
                aria-label='Service level'
              >
                {isLegacyLevel && (
                  <option value={service.serviceName} disabled>
                    {service.serviceName} (not recognised — pick a level)
                  </option>
                )}
                {SERVICE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
              <button
                type='button'
                onClick={onClose}
                className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors shrink-0'
                aria-label='Close'
              >
                <MdClose className='w-5 h-5' />
              </button>
            </div>
            <p className='text-xs text-gray-500 mt-1 pl-2'>
              Zone: {zone.zoneName}
              {isPalletService ? ` · ${getPalletTypeLabel(service.palletType)}` : ''}
            </p>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto p-4 space-y-4'>
          <div>
            <label className='block text-xs font-medium text-gray-500 mb-1'>Unit</label>
            <select value={service.unit} onChange={(e) => changeUnit(e.target.value)} className={selectClass}>
              {SHIPPING_UNITS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>

          {isPalletService && (
            <PalletVisualizer
              dimensions={pricing?.palletDimensions ?? getDefaultPalletCaps(service.palletType) ?? EMPTY_CAPS}
              palletType={service.palletType}
              onDimensionsChange={(dimensions) => onPalletCapsChange(service, dimensions)}
              onPalletTypeChange={changePalletType}
            />
          )}

          {!isPalletService && service.unit !== 'FTL' && (
            <div>
              <label className='block text-xs font-medium text-gray-500 mb-1'>
                {UNIT_LABELS[service.unit] || service.unit}
              </label>
              <input
                type='number'
                value={pricing?.unitValue ?? ''}
                onChange={(e) =>
                  onUnitValueChange(zone.id, service, e.target.value ? parseFloat(e.target.value) : null)
                }
                className={inputClass}
                step='0.01'
                placeholder='0.00'
              />
            </div>
          )}

          {!useTiers && (
            <div>
              <label className='block text-xs font-medium text-gray-500 mb-2'>Price</label>
              <input
                type='number'
                value={breaks[0]?.price || ''}
                onChange={(e) => updateBreak(0, 'price', parseFloat(e.target.value) || 0)}
                className={inputClass}
                step='0.01'
                placeholder='£0.00'
              />
            </div>
          )}

          <div className='flex items-center justify-between pt-2 border-t border-gray-100'>
            <span className='text-xs text-gray-500'>Tiered pricing</span>
            <button
              type='button'
              onClick={handleToggleTiers}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                useTiers ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  useTiers ? 'translate-x-4.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {useTiers && (
            <div>
              <div className='flex items-center justify-between mb-2'>
                <label className='text-xs font-medium text-gray-500'>Quantity Breaks</label>
                <button
                  type='button'
                  onClick={addBreak}
                  className='flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium'
                >
                  <MdAdd className='w-3 h-3' />
                  Add Tier
                </button>
              </div>

              <div className='space-y-2'>
                <div className='grid grid-cols-4 gap-2 text-xs font-medium text-gray-400 px-1'>
                  <span>Min</span>
                  <span>Max</span>
                  <span>Price</span>
                  <span />
                </div>

                {breaks.map((qBreak, index) => (
                  <div key={index} className='grid grid-cols-4 gap-2'>
                    <input
                      type='number'
                      value={qBreak.min}
                      onChange={(e) => updateBreak(index, 'min', parseFloat(e.target.value) || 0)}
                      className={inputClass}
                      step='0.01'
                      min={0}
                    />
                    <input
                      type='number'
                      value={qBreak.max ?? ''}
                      onChange={(e) =>
                        updateBreak(index, 'max', e.target.value ? parseFloat(e.target.value) : null)
                      }
                      className={inputClass}
                      step='0.01'
                      placeholder='∞'
                    />
                    <input
                      type='number'
                      value={qBreak.price || ''}
                      onChange={(e) => updateBreak(index, 'price', parseFloat(e.target.value) || 0)}
                      className={inputClass}
                      step='0.01'
                      placeholder='£'
                    />
                    <div className='flex items-center'>
                      {breaks.length > 1 && (
                        <button
                          type='button'
                          onClick={() => removeBreak(index)}
                          className='p-1 text-gray-400 hover:text-red-500 transition-colors'
                        >
                          <MdDelete className='w-4 h-4' />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='pt-2 border-t border-gray-100'>
            {isConfirmingRemoval ? (
              <div className='rounded-lg border border-red-200 bg-red-50 p-3'>
                <p className='text-sm text-red-800'>{`Remove ${getServiceLabel(service)} from every zone?`}</p>
                <div className='flex gap-2 mt-2'>
                  <button
                    type='button'
                    onClick={() => onRemoveService(service)}
                    className='px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors'
                  >
                    Remove
                  </button>
                  <button
                    type='button'
                    onClick={() => setIsConfirmingRemoval(false)}
                    className='px-3 py-1.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors'
                  >
                    Keep
                  </button>
                </div>
              </div>
            ) : (
              <button
                type='button'
                onClick={() => setIsConfirmingRemoval(true)}
                className='flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700'
              >
                <MdDelete className='w-4 h-4' />
                Remove service
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
